<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout v-if="userAuthStore.plusFreeAccess.enabled" />

    <search-header
      :title="pageTitle"
      active="filters"
      :filter-url="filterUrl"
      :show-tags="true"
      @tag-update="tagUpdate"
      @clear-all="clearAll"
    />

    <div ref="contentResultsEl" class="content-type-options">
      <search-filters
        :loading="page.loading"
        :show-keywords="true"
        :show-tags="true"
        :entity-type="entityType"
        :entity-category="entityCategory"
        @filter-update="filterUpdate"
      >
        <recipe-filters @filter-update="filterUpdate" />
      </search-filters>

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort :total="searchResults.total" @sort-update="sortUpdate" />
          <search-result-actions
            :categories="isMyFavoritesPage && userAuthStore.features.EXCLUSIVE_FAVORITES"
            :active-expander="activeExpander"
            :accessibility-expander-id="accessibilityExpanderId"
            @expander-click="toggleActiveExpander"
          />
        </div>

        <!-- expander -->
        <div :id="accessibilityExpanderId" class="results-expander" :aria-hidden="!activeExpander">
          <user-favorites-nav
            v-if="
              isMyFavoritesPage &&
              userAuthStore.features.EXCLUSIVE_FAVORITES &&
              activeExpander === 'categories'
            "
            :active-nav-type="entityCategory"
          />
        </div>

        <!-- results -->
        <search-result-cards
          v-if="!errorMsg && (searchResults.loading || searchResults.total > 0)"
          v-slot="{ cardOrder }"
          :loading="searchResults.loading"
          :per-page-total="searchResults.perPage"
          :current-page-total="searchResultsData.length"
          :entity-type="entityType"
        >
          <article-card
            v-for="(article, index) in searchResultsData"
            :key="article.id"
            :article="article"
            :plus-pass="searchStore.plusPass"
            :cheapest-monthly-price="searchStore.cheapestMonthlyPrice"
            :style="{ order: cardOrder(index) }"
          />
        </search-result-cards>
        <search-no-results v-else @clear-all="clearAll" />

        <!-- pagination -->
        <pagination
          :current-page="searchResults.currentPage"
          :total-pages="searchResults.totalPages"
          :total-items="searchResults.total"
          :per-page="searchResults.perPage"
          @page-update="pageUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import Pagination from '../search/pagination.vue'
import SearchHeader from '@/components/search/search-header.vue'
import SearchFilters from '@/components/search/search-filters.vue'
import SearchSort from '@/components/search/search-sort.vue'
import SearchResultActions from '@/components/search/search-result-actions.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import type { ArticleCardType } from '@/types/model-types'
import { removeArraySyntax } from '@/utils/helpers'
import { fbApi, isAbortError, objToSearchParams } from '@/utils/http-api'
import type {
  ArticlePagination,
  DropdownFilter,
  InitCheckboxFilter,
  InitCheckboxFilterItem,
  PlusPass,
  RangeFilter,
  RecipeFacets,
  SearchTeamMember,
  SortOption,
} from '@/types/search-types'
import Callout from '@/components/notifications/callout.vue'
import RecipeFilters from '@/components/articles/recipe-filters.vue'
import SearchResultCards from '@/components/search/search-result-cards.vue'
import ArticleCard from '@/components/articles/article-card.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import UserFavoritesNav from '@/components/users/user-favorites-nav.vue'
import { useSearchRoute } from '@/composables/search-route'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'

/** TYPES **/
type ActiveExpander = 'categories' | ''

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const {
  contentResultsEl,
  contentOrgEl,
  tagUpdate,
  filterUpdate,
  sortUpdate,
  clearAll,
  pageUpdate,
} = useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
  currentPage: number
  totalPages: number
  total: number
  perPage: number
}>({
  loading: false,
  error: '',
  currentPage: 0,
  totalPages: 0,
  perPage: 20,
  total: 0,
})

const searchResultsData = shallowRef<ArticleCardType[]>([])

const activeExpander = ref<ActiveExpander>('')

/** NON REACTIVE **/
let searchInitAbort: AbortController | null = null
let fetchAbort: AbortController | null = null

const accessibilityExpanderId = 'results-expander-control'
const entityType = 'article'
const entityCategory = 'recipe'

const checkboxFilters: Record<string, InitCheckboxFilter> = {
  is_exclusive: {
    label: 'Membership',
    name: 'exclusive',
    items: [
      { label: 'Free', value: 0 },
      { label: 'Plus', value: 1 },
    ],
  },
  recipetype_ids: {
    label: 'Recipe Type',
    name: 'recipetype',
    items: [
      { label: 'Complete Meal', value: 91 },
      { label: 'Individual Recipe', value: 92 },
    ],
  },
  mealtype_ids: {
    label: 'Meal Type',
    name: 'mealtype',
    items: [
      { label: 'Breakfast', value: 35 },
      { label: 'Lunch', value: 36 },
      { label: 'Dinner', value: 37 },
      { label: 'Snack', value: 38 },
      { label: 'Dessert', value: 39 },
      { label: 'Side Dish', value: 43 },
    ],
  },
  dietarytype_ids: {
    label: 'Dietary Type',
    name: 'dietarytype',
    items: [
      { label: 'Omnivore', value: 40 },
      { label: 'Vegetarian', value: 41 },
      { label: 'Vegan', value: 42 },
    ],
  },
  team_member_ids: {
    label: 'Team Member',
    name: 'teammember',
    items: [], // items come from searchInit
  },
}

const dropdownFilters: Record<string, DropdownFilter> = {
  tags: {
    label: 'Tags',
    name: 'tags',
    multiple: true,
    value: [],
  },
}

const rangeFilters: Record<string, RangeFilter> = {
  prep_time: {
    name: 'prep_time',
    label: 'Prep Time',
    units: 'Prep Time',
    min: { name: 'minprep', value: '' },
    max: { name: 'maxprep', value: '' },
  },
  cook_time: {
    name: 'cook_time',
    label: 'Cook Time',
    units: 'Cook Time',
    min: { name: 'mincook', value: '' },
    max: { name: 'maxcook', value: '' },
  },
  serving_size: {
    name: 'serving_size',
    label: 'Servings',
    units: 'Servings',
    min: { name: 'minserves', value: '' },
    max: { name: 'maxserves', value: '' },
  },
}

const sortOptions: SortOption[] = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Most Popular', value: 'popular' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Relevance', value: 'relevance' },
]

/** COMPUTED **/
const isMyFavoritesPage = computed(() => route.name === 'FavoriteRecipes')
const pageTitle = computed(() => (isMyFavoritesPage.value ? 'Favorite Recipes' : 'Recipes'))
const filterUrl = computed(() =>
  isMyFavoritesPage.value ? '/my/favorite/recipes' : '/healthy-living/healthy-recipes',
)

const errorMsg = computed(() => page.value.error || searchResults.value.error)

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync on every query string change
    syncUrlToState({ init: false })

    // refresh data
    fetchRecipes()
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  searchStore.setSortOptions(sortOptions)

  // allow search init to finish first before fetching recipes,
  // it will populate team members before syncing state from url
  await searchInit()

  // searchInit doesn't re-throw errors, so only continue if it set data
  // helps avoid users quickly toggling between filter and browse views since
  // this method is async
  if (!(searchStore.init.plusPass && searchStore.init.cheapestMonthlyPrice)) {
    return
  }

  syncUrlToState({ init: true })

  // do not wait for this to finish before moving on
  fetchRecipes()
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (searchInitAbort) {
    searchInitAbort.abort()
  }

  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({ init }: { init: boolean }) {
  // removes [] and forces array for items with [] in var names
  const queryParams = removeArraySyntax(route.query)

  // filters
  searchStore.setCheckboxFilters({
    checkboxFilters,
    queryStringFilters: queryParams,
    checkboxUrlParameterNames: searchStore.createIncludeExcludeParamMap(
      {
        teammember: 'team_member_ids',
        recipetype: 'recipetype_ids',
        dietarytype: 'dietarytype_ids',
        mealtype: 'mealtype_ids',
        exclusive: 'is_exclusive',
      },
      userAuthStore.features.ARTICLE_FILTERS,
    ),
    init,
  })

  searchStore.setDropdownFilters({
    dropdownFilters,
    queryStringFilters: queryParams,
    dropdownUrlParameterNames: {
      tags: 'tags',
    },
    init,
  })

  searchStore.setRangeFilters({
    rangeFilters,
    queryStringFilters: queryParams,
    rangeUrlParameterNames: {
      minprep: { filterName: 'prep_time', field: 'min' },
      maxprep: { filterName: 'prep_time', field: 'max' },
      mincook: { filterName: 'cook_time', field: 'min' },
      maxcook: { filterName: 'cook_time', field: 'max' },
      minserves: { filterName: 'serving_size', field: 'min' },
      maxserves: { filterName: 'serving_size', field: 'max' },
    },
    init,
  })

  searchStore.setKeywordsAndSort(queryParams.keywords || '', queryParams.sort || 'newest')

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

async function fetchRecipes() {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // format GET query params
  let params = route.query || {}
  // NOTE: category is actually hardcoded in both ArticlesController index/recipeIndex methods and ArticlesApiController for now
  params.category = ['4']
  params = removeArraySyntax(params)

  // reset pagination on a filter change
  if (!('page' in params)) {
    searchResults.value.currentPage = 1
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const apiUrl = isMyFavoritesPage.value ? '/articles/favorites' : '/articles'

    const res = await fbApi.get(apiUrl, {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch recipes, please refresh the page and try again`)
    }

    const {
      paginatedArticles,
      facets,
    }: {
      paginatedArticles: ArticlePagination
      facets: RecipeFacets
    } = res.body.data

    searchResults.value.total = paginatedArticles.total
    searchResults.value.currentPage = paginatedArticles.current_page
    searchResults.value.totalPages = paginatedArticles.last_page
    searchResultsData.value = paginatedArticles.data

    // pinia facets
    searchStore.updateFacets(facets)
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchArticles aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch recipes, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}

/**
 * No loading state, should happen first
 */
async function searchInit() {
  // clear any error and set both loading stats so skeleton cards show
  page.value.error = ''
  page.value.loading = true
  searchResults.value.loading = true

  let params = route.query || {}
  // NOTE: category is actually hardcoded in both ArticlesController index/recipeIndex methods and ArticlesApiController for now
  params.category = ['4']
  params = removeArraySyntax(params)

  try {
    searchInitAbort = new AbortController()

    const res = await fbApi.get('/articles/search-init', {
      searchParams: objToSearchParams(params),
      signal: searchInitAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch recipes, please refresh the page and try again`)
    }

    const {
      plusPass,
      cheapestMonthlyPrice,
      teamMembers,
    }: {
      plusPass: PlusPass
      cheapestMonthlyPrice: string
      teamMembers: SearchTeamMember[]
    } = res.body.data

    searchStore.setSearchInit({
      plusPass,
      cheapestMonthlyPrice,
    })

    // add team members to filters
    if (teamMembers) {
      checkboxFilters.team_member_ids.items = teamMembers.map(
        (teamMember): InitCheckboxFilterItem => ({
          label: teamMember.name,
          value: teamMember.id,
          thumbnail: teamMember.thumbnail,
        }),
      )
    }
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      return
    }

    // oops model will show
    console.error(err)
    page.value.error = `Unable to initialize search, please refresh the page and try again`
  } finally {
    page.value.loading = false
  }
}

function toggleActiveExpander(expander: ActiveExpander) {
  if (activeExpander.value === expander) {
    activeExpander.value = ''
    return
  }

  activeExpander.value = expander
}
</script>
